import { Injectable, signal, inject } from '@angular/core';
import { injectQueryClient, mutationOptions, queryOptions } from '@tanstack/angular-query-experimental';
import { BackendService } from './backend.service';
import { DataService } from './data.service';
import { Observable, defer, firstValueFrom, from, of } from 'rxjs';
import { tap, switchMap, take } from 'rxjs/operators';
import { PlaidService } from './plaid.service';
import {
  Account,
  CustomAccount,
  Credential,
  CredentialPage,
  CredentialAccountUpdateChanges,
  UpdateCredentialAccountPayload,
} from '../types';
import {
  CreateCredentialRequest,
  CreateCustomCredentialAccountRequest,
  FinancialAccount,
  FinancialCredentialPage,
  LinkToken,
  UpdateCustomCredentialAccountRequest,
  UpdatePlaidAccountRequest,
} from '../generated-api/models';
import { ErrorMessagingService } from './error.service';
import { AnalyticsService } from './analytics.service';
import {
  EVENT_ADDED_ACCOUNT,
  EVENT_ADDED_ACCOUNT_NICKNAME,
  EVENT_ADDED_CUSTOM_ACCOUNT,
  EVENT_DISABLED_ACCOUNT_SYNCING,
  EVENT_ENABLED_ACCOUNT_SYNCING,
} from '../analytics/events';
import { DateTime } from 'luxon';
import { accountQueryKeys, groupQueryKeys } from './query-keys';
import { Capacitor } from '@capacitor/core';
import { NativeLinkService } from './native-link.service';
import { HostedLink } from './hosted-link.plugin';
import { NativeBrowserService } from './native-browser.service';

type PlaidLinkToken = LinkToken & {
  hostedLinkUrl?: string | null;
  hostedLinkSession?: string | null;
};
type HostedLinkCompletion = {
  publicToken: string | null;
  credentialId: string | null;
};
type PlaidAccountUpdateRequestDraft = Omit<UpdateCredentialAccountPayload, 'syncStartDate' | 'syncDisabled'> &
  Pick<Account, 'syncStartDate' | 'syncDisabled'>;
type InitializePlaidLinkOptions = {
  path?: string;
  receivedRedirectUri?: string;
  credentialId?: string;
  syncStartDate?: string;
  onLoad?: () => void;
  onCredentialCreated?: () => void;
  /** Receives the reconnected credential, so a caller can ignore a session it didn't start. */
  onCredentialReauthenticated?: (credentialId: string) => void;
  /** The member closed Link without finishing (not called when an OAuth bank leaves the page). */
  onExit?: () => void;
};
type CustomAccountCreateMutationVariables = {
  data: CreateCustomCredentialAccountRequest;
  credentialId: string;
};
type CustomAccountEditMutationVariables = {
  data: UpdateCustomCredentialAccountRequest & { id: string };
  credentialId: string;
};
type CustomAccountDeleteMutationVariables = {
  data: Pick<CustomAccount, 'id'>;
  credentialId: string;
};
type CredentialAccountUpdateMutationVariables = {
  credentialId: string;
  account: Account;
  changes: CredentialAccountUpdateChanges;
};
@Injectable({ providedIn: 'root' })
export class AccountService {
  private analytics = inject(AnalyticsService);
  private backend = inject(BackendService);
  private data = inject(DataService);
  private plaidService = inject(PlaidService);
  private errorMessaging = inject(ErrorMessagingService);
  private nativeLinks = inject(NativeLinkService);
  private nativeBrowser = inject(NativeBrowserService);

  private readonly queryClient = injectQueryClient();
  private readonly creatingCredentialSignal = signal(false);

  get creatingCredential(): boolean {
    return this.creatingCredentialSignal();
  }

  set creatingCredential(creatingCredential: boolean) {
    this.creatingCredentialSignal.set(creatingCredential);
  }

  // If a credentialId is passed, that means Link is being opened in update mode (re-authenticating a credential, not creating a new one.)
  // Therefore we need to fetch a separate token from the backend.
  // Otherwise we can used the cached token
  private fetchLinkToken$(
    path: string,
    credentialId?: string,
    hosted = false,
  ): Observable<PlaidLinkToken> {
    const cacheKey = `plaid-link-token-${hosted ? 'hosted-' : ''}${path}`;
    const cachedToken: PlaidLinkToken | null = credentialId
      ? null
      : this.data.getSessionStorage<PlaidLinkToken>(cacheKey);

    if (cachedToken && cachedToken.expires && DateTime.fromISO(cachedToken.expires) > DateTime.local())
      return of(cachedToken);
    return this.backend
      .request<PlaidLinkToken>({
        type: 'get',
        apiRoute: `credential/linkToken`,
        params: { path, credentialId, hosted },
      })
      .pipe(tap(token => this.data.setSessionStorage(cacheKey, token)));
  }

  initializePlaidLinkMutationOptions() {
    return mutationOptions({
      mutationKey: ['credential', 'plaid-link', 'initialize'],
      mutationFn: (options: InitializePlaidLinkOptions = {}) => this.initializePlaidLink(options),
    });
  }

  private initializePlaidLink(options: InitializePlaidLinkOptions = {}): Promise<PlaidLinkHandler> {
    return firstValueFrom(this.initializePlaidLink$(options));
  }

  private initializePlaidLink$({
    path,
    receivedRedirectUri,
    credentialId,
    syncStartDate,
    onLoad,
    onCredentialCreated,
    onCredentialReauthenticated,
    onExit,
  }: InitializePlaidLinkOptions = {}): Observable<PlaidLinkHandler> {
    const configuredPath = createPlaidRedirectUri(path, window.location);
    const hosted = Capacitor.isNativePlatform();
    return this.fetchLinkToken$(configuredPath, credentialId, hosted).pipe(
      switchMap(res =>
        hosted
          ? this.initializeHostedPlaid$(res, {
              configuredPath,
              syncStartDate,
              onLoad,
              onCredentialCreated,
              onCredentialReauthenticated,
            })
          : this.plaidService.initializePlaid$({
              token: res.token,
              receivedRedirectUri,
              onSuccess: () => {
                if (res.credentialId) {
                  const credentialId = res.credentialId;
                  this.data.removeSessionStorage(`plaid-link-token-${configuredPath}`);
                  this.runPlaidLinkEffect(
                    this.reauthenticateCredential$(res.credentialId).pipe(
                      tap(() => onCredentialReauthenticated?.(credentialId)),
                    ),
                    'Failed to reconnect accounts',
                  );
                  return;
                }

                const linkSession = res.hostedLinkSession;
                if (!linkSession) throw new Error('Plaid Link did not return a completion session');

                this.runPlaidLinkEffect(
                  this.completePlaidLink$(linkSession).pipe(
                    switchMap(completion => {
                      if (!completion.credentialId) {
                        return this.configureAccounts$(linkSession, syncStartDate).pipe(
                          tap(() => onCredentialCreated?.()),
                        );
                      }

                      return this.initializePlaidLink$({
                        path: configuredPath,
                        credentialId: completion.credentialId,
                        onCredentialReauthenticated,
                      }).pipe(
                        tap(handler => handler.open()),
                        switchMap(() => of(undefined)),
                      );
                    }),
                  ),
                  'Failed to authenticate accounts',
                );
              },
              onExit: () => {
                this.data.removeSessionStorage(`plaid-link-token-${configuredPath}`);
                onExit?.();
              },
              onLoad,
            }),
      ),
      take(1),
    );
  }

  private completePlaidLink$(linkSession: string | null | undefined): Observable<HostedLinkCompletion> {
    if (!linkSession) throw new Error('Plaid Link did not return a completion session');
    return this.backend.request<HostedLinkCompletion, { hostedLinkSession: string }>({
      type: 'post',
      apiRoute: 'credential/linkToken/complete',
      data: { hostedLinkSession: linkSession },
    });
  }

  private initializeHostedPlaid$(
    linkToken: PlaidLinkToken,
    options: {
      configuredPath: string;
      syncStartDate?: string;
      onLoad?: () => void;
      onCredentialCreated?: () => void;
      onCredentialReauthenticated?: (credentialId: string) => void;
    },
  ): Observable<PlaidLinkHandler> {
    const hostedLinkUrl = linkToken.hostedLinkUrl;
    const hostedLinkSession = linkToken.hostedLinkSession;
    if (!hostedLinkUrl || !hostedLinkSession) throw new Error('Plaid Hosted Link did not return a launch URL');

    const cacheKey = `plaid-link-token-hosted-${options.configuredPath}`;
    const completeHostedLink = () =>
      this.backend
        .request<HostedLinkCompletion, { hostedLinkSession: string }>({
          type: 'post',
          apiRoute: 'credential/linkToken/complete',
          data: { hostedLinkSession },
        })
        .pipe(
          switchMap(completion => {
            this.data.removeSessionStorage(cacheKey);
            if (completion.credentialId) {
              if (!linkToken.credentialId) {
                return this.initializePlaidLink$({
                  path: options.configuredPath,
                  credentialId: completion.credentialId,
                  onCredentialReauthenticated: options.onCredentialReauthenticated,
                }).pipe(
                  tap(handler => handler.open()),
                  switchMap(() => of(undefined)),
                );
              }
              const credentialId = completion.credentialId;
              return this.reauthenticateCredential$(credentialId).pipe(
                tap(() => options.onCredentialReauthenticated?.(credentialId)),
              );
            }
            if (!completion.publicToken) throw new Error('Plaid Hosted Link did not return a public token');
            return this.configureAccounts$(hostedLinkSession, options.syncStartDate).pipe(
              tap(() => options.onCredentialCreated?.()),
            );
          }),
        );

    return of({
      open: (): void => {
        options.onLoad?.();
        const isIos = Capacitor.getPlatform() === 'ios';
        const completion$ = isIos
          ? defer(() => from(HostedLink.open({ url: hostedLinkUrl, callbackScheme: 'budgets' }))).pipe(
              switchMap(() => completeHostedLink()),
            )
          : this.nativeLinks.hostedPlaidCompleted$.pipe(
              take(1),
              tap(() => void this.nativeBrowser.close()),
              switchMap(() => completeHostedLink()),
            );

        completion$.subscribe({
          error: error => {
            this.creatingCredential = false;
            this.errorMessaging.backendError(error, 'Failed to authenticate accounts');
          },
        });
        if (!isIos) {
          void this.nativeBrowser.open(hostedLinkUrl).catch(error => {
            this.errorMessaging.backendError(error, 'Failed to open account connection');
          });
        }
      },
      exit: (): void => {
        void this.nativeBrowser.close();
      },
      destroy: (): void => undefined,
      institutions: [],
    });
  }

  setAccountData(accountData: CredentialPage): void {
    this.queryClient.setQueryData(accountQueryKeys.page(), accountData);
  }

  private invalidateAndRefreshAccountData$(): Observable<CredentialPage | void> {
    return defer(() => from(this.invalidateAndRefreshAccountData()));
  }

  private async invalidateAndRefreshAccountData(): Promise<CredentialPage> {
    await this.cancelActiveAccountPageQuery();
    await Promise.all([
      this.queryClient.invalidateQueries({ queryKey: accountQueryKeys.page(), refetchType: 'none' }),
      this.queryClient.invalidateQueries({ queryKey: groupQueryKeys.all }),
    ]);
    return this.refreshAccountPage();
  }

  private createCredential$(data: CreateCredentialRequest): Observable<CredentialPage | void> {
    return defer(() => from(this.createCredential(data)));
  }

  private async createCredential(data: CreateCredentialRequest): Promise<CredentialPage> {
    this.creatingCredential = true;
    await firstValueFrom(
      this.backend.request<void, CreateCredentialRequest>({
        type: 'post',
        apiRoute: 'credential',
        data,
      }),
    );
    const page = await this.invalidateAndRefreshAccountData();
    this.creatingCredential = false;
    this.analytics.sendEvent(EVENT_ADDED_ACCOUNT);
    return page;
  }

  deleteCredentialMutationOptions() {
    return mutationOptions({
      mutationKey: ['credential', 'delete'],
      mutationFn: (credential: Pick<Credential, 'id'>) => this.deleteCredential(credential),
    });
  }

  deleteCredential$(credential: Pick<Credential, 'id'>): Observable<CredentialPage | void> {
    return defer(() => from(this.deleteCredential(credential)));
  }

  deleteCredential(credential: Pick<Credential, 'id'>): Promise<CredentialPage> {
    return firstValueFrom(
      this.backend.request<void>({
        type: 'delete',
        apiRoute: `credential/${credential.id}`,
      }),
    ).then(() => this.invalidateAndRefreshAccountData());
  }

  private reauthenticateCredential$(credentialId: string): Observable<CredentialPage | void> {
    return defer(() => from(this.reauthenticateCredential(credentialId)));
  }

  async reauthenticateCredential(credentialId: string): Promise<CredentialPage> {
    const previousPage = this.queryClient.getQueryData<CredentialPage>(accountQueryKeys.page());
    await this.cancelActiveAccountPageQuery();
    this.setCredentialReauthenticating(credentialId);
    try {
      await firstValueFrom(
        this.backend.request<void>({
          type: 'update',
          apiRoute: `credential/reauthenticate/${credentialId}`,
        }),
      );
      return await this.invalidateAndRefreshAccountData();
    } catch (error) {
      if (previousPage) this.setAccountData(previousPage);
      throw error;
    }
  }

  syncPlaidCredentialsMutationOptions() {
    return mutationOptions({
      mutationKey: ['credential', 'sync'],
      mutationFn: () => this.syncPlaidCredentials(),
    });
  }

  retryPlaidCredentialSyncMutationOptions() {
    return mutationOptions({
      mutationKey: ['credential', 'sync', 'retry'],
      mutationFn: (credentialId: string) => this.retryPlaidCredentialSync(credentialId),
    });
  }

  private retryPlaidCredentialSync(credentialId: string): Promise<{ syncedCredentialCount: number }> {
    return firstValueFrom(
      this.backend.request<{ syncedCredentialCount: number }>({
        type: 'post',
        apiRoute: `credential/${credentialId}/sync`,
        timeoutMillis: 15000,
      }),
    ).then(response => {
      this.invalidateAccountData();
      void this.queryClient.invalidateQueries({ queryKey: groupQueryKeys.all });
      return response;
    });
  }

  private syncPlaidCredentials(): Promise<{ syncedCredentialCount: number }> {
    return firstValueFrom(
      this.backend.request<{ syncedCredentialCount: number }>({
        type: 'post',
        apiRoute: 'credential/sync',
        timeoutMillis: 15000,
      }),
    ).then(response => {
      this.invalidateAccountData();
      void this.queryClient.invalidateQueries({ queryKey: groupQueryKeys.all });
      return response;
    });
  }

  invalidateAccountData(): void {
    void this.queryClient.invalidateQueries({ queryKey: accountQueryKeys.page() });
  }

  private configureAccounts$(linkSession: string, syncStartDate?: string): Observable<CredentialPage | void> {
    return this.createCredential$({
      linkSession,
      ...(syncStartDate == null ? {} : { syncStartDate }),
    });
  }

  addCustomAccountMutationOptions() {
    return mutationOptions({
      mutationKey: ['credential', 'custom-account', 'create'],
      mutationFn: ({ data, credentialId }: CustomAccountCreateMutationVariables) =>
        this.addCustomAccount(data, credentialId),
    });
  }

  addCustomAccount$(
    data: CreateCustomCredentialAccountRequest,
    credentialId: string,
  ): Observable<CredentialPage | void> {
    return defer(() => from(this.addCustomAccount(data, credentialId)));
  }

  async addCustomAccount(data: CreateCustomCredentialAccountRequest, credentialId: string): Promise<CredentialPage> {
    await firstValueFrom(
      this.backend.request<void, CreateCustomCredentialAccountRequest>({
        type: 'post',
        apiRoute: `credential/custom/${credentialId}/account`,
        data,
      }),
    );
    const page = await this.invalidateAndRefreshAccountData();
    this.creatingCredential = false;
    this.analytics.sendEvent(EVENT_ADDED_CUSTOM_ACCOUNT);
    return page;
  }

  editCustomAccountMutationOptions() {
    return mutationOptions({
      mutationKey: ['credential', 'custom-account', 'edit'],
      mutationFn: ({ data, credentialId }: CustomAccountEditMutationVariables) =>
        this.editCustomAccount(data, credentialId),
    });
  }

  editCustomAccount$(
    data: UpdateCustomCredentialAccountRequest & { id: string },
    credentialId: string,
  ): Observable<CredentialPage | void> {
    return defer(() => from(this.editCustomAccount(data, credentialId)));
  }

  async editCustomAccount(
    data: UpdateCustomCredentialAccountRequest & { id: string },
    credentialId: string,
  ): Promise<CredentialPage> {
    const { id, ...payload } = data;

    await firstValueFrom(
      this.backend.request<void, UpdateCustomCredentialAccountRequest>({
        type: 'update',
        apiRoute: `credential/custom/${credentialId}/account/${id}`,
        data: payload,
      }),
    );
    const page = await this.invalidateAndRefreshAccountData();
    this.creatingCredential = false;
    return page;
  }

  private updateCredentialAccount$(credentialId: string, data: PlaidAccountUpdateRequestDraft): Observable<Account> {
    const payload: UpdatePlaidAccountRequest = {
      syncStartDate: data.syncStartDate,
      syncDisabled: data.syncDisabled ?? false,
      includeInAvailableBalance: data.includeInAvailableBalance,
      ...(typeof data.nickname === 'string' ? { nickname: data.nickname } : {}),
    };

    return this.backend
      .request<Account & FinancialAccount, UpdatePlaidAccountRequest>({
        type: 'update',
        apiRoute: `credential/${credentialId}/account/${data.id}`,
        data: payload,
      })
      .pipe(take(1));
  }

  updateExistingCredentialAccountMutationOptions() {
    return mutationOptions({
      mutationKey: ['credential', 'account', 'update'],
      mutationFn: ({ credentialId, account, changes }: CredentialAccountUpdateMutationVariables) =>
        this.updateExistingCredentialAccount(credentialId, account, changes),
    });
  }

  async updateExistingCredentialAccount(
    credentialId: string,
    account: Account,
    changes: CredentialAccountUpdateChanges,
  ): Promise<Account> {
    const updatedAccount = await firstValueFrom(
      this.updateCredentialAccount$(credentialId, this.buildCredentialAccountUpdatePayload(account, changes)),
    );
    await this.cancelActiveAccountPageQuery();
    this.applyCredentialAccountUpdate(account, updatedAccount);
    await this.invalidateAndRefreshAccountData();
    return updatedAccount;
  }

  buildCredentialAccountUpdatePayload(
    account: Account,
    changes: CredentialAccountUpdateChanges,
  ): PlaidAccountUpdateRequestDraft {
    return {
      id: account.id,
      ...(typeof changes.nickname === 'string' ? { nickname: changes.nickname } : {}),
      syncDisabled: changes.syncDisabled ?? account.syncDisabled ?? false,
      includeInAvailableBalance: changes.includeInAvailableBalance ?? account.includeInAvailableBalance,
      syncStartDate: this.formatPlaidSyncStartDateForUpdate(changes.syncStartDate ?? account.syncStartDate),
    };
  }

  private formatPlaidSyncStartDateForUpdate(
    value: CredentialAccountUpdateChanges['syncStartDate'] | Account['syncStartDate'] | null | undefined,
  ): string {
    if (value instanceof Date) return DateTime.fromJSDate(value, { zone: 'utc' }).toFormat('yyyy-MM-dd');

    if (value == null || value === '') return DateTime.local().toFormat('yyyy-MM-dd');

    const parsed = DateTime.fromISO(value, { zone: 'utc' });
    return parsed.isValid ? parsed.toFormat('yyyy-MM-dd') : DateTime.local().toFormat('yyyy-MM-dd');
  }

  applyCredentialAccountUpdate(account: Account, updatedAccount: Account): void {
    if (updatedAccount.nickname !== account.nickname) {
      account.nickname = updatedAccount.nickname;
      this.analytics.sendEvent(EVENT_ADDED_ACCOUNT_NICKNAME);
    }

    if (account.syncDisabled !== updatedAccount.syncDisabled) {
      if (updatedAccount.syncDisabled) this.analytics.sendEvent(EVENT_DISABLED_ACCOUNT_SYNCING);
      else this.analytics.sendEvent(EVENT_ENABLED_ACCOUNT_SYNCING);

      account.syncDisabled = updatedAccount.syncDisabled;
    }

    account.includeInAvailableBalance = updatedAccount.includeInAvailableBalance;

    account.syncStartDate = updatedAccount.syncStartDate;
    this.applyAccountUpdateToCachedPage(updatedAccount);
  }

  deleteCustomAccount$(data: Pick<CustomAccount, 'id'>, credentialId: string): Observable<CredentialPage | void> {
    return defer(() => from(this.deleteCustomAccount(data, credentialId)));
  }

  deleteCustomAccountMutationOptions() {
    return mutationOptions({
      mutationKey: ['credential', 'custom-account', 'delete'],
      mutationFn: ({ data, credentialId }: CustomAccountDeleteMutationVariables) =>
        this.deleteCustomAccount(data, credentialId),
    });
  }

  async deleteCustomAccount(data: Pick<CustomAccount, 'id'>, credentialId: string): Promise<CredentialPage> {
    await firstValueFrom(
      this.backend.request<void>({
        type: 'delete',
        apiRoute: `credential/custom/${credentialId}/account/${data.id}`,
      }),
    );
    const page = await this.invalidateAndRefreshAccountData();
    this.creatingCredential = false;
    return page;
  }

  private setCredentialReauthenticating(credentialId: string): void {
    this.queryClient.setQueryData<CredentialPage>(accountQueryKeys.page(), page => {
      if (!page) return page;
      return {
        ...page,
        data: page.data.map(credential =>
          credential.id === credentialId ? { ...credential, reauthenticating: true } : credential,
        ),
      };
    });
  }

  private applyAccountUpdateToCachedPage(updatedAccount: Account): void {
    const page = this.queryClient.getQueryData<CredentialPage>(accountQueryKeys.page());
    if (!page) return;

    this.setAccountData({
      ...page,
      data: page.data.map(credential => ({
        ...credential,
        accounts: credential.accounts.map(account =>
          account.id === updatedAccount.id ? { ...account, ...updatedAccount } : account,
        ),
      })),
    });
  }

  accountPageQueryOptions() {
    return queryOptions({
      queryKey: accountQueryKeys.page(),
      queryFn: () => this.fetchAccountPageFromBackend(),
      staleTime: 0,
      refetchOnMount: 'always',
    });
  }

  private refreshAccountPage(): Promise<CredentialPage> {
    return this.fetchAccountPageFromBackend().then(page => {
      this.setAccountData(page);
      return page;
    });
  }

  private fetchAccountPageFromBackend(): Promise<CredentialPage> {
    return firstValueFrom(
      this.backend.request<CredentialPage & FinancialCredentialPage>({
        type: 'get',
        apiRoute: 'credential',
      }),
    );
  }

  private cancelActiveAccountPageQuery(): Promise<void> {
    return this.queryClient.cancelQueries({ queryKey: accountQueryKeys.page() });
  }

  private runPlaidLinkEffect(request$: Observable<unknown>, baseMessage: string): void {
    request$.pipe(take(1)).subscribe({
      error: error => {
        this.creatingCredential = false;
        this.errorMessaging.backendError(error, baseMessage);
      },
    });
  }
}

export function createPlaidRedirectUri(path: string | undefined, location: Location): string {
  return new URL(path || location.pathname, location.origin).toString();
}
